import axios from 'axios';
import config from '../config/default';
import { getCookie, setCookie } from '../utils';

/**
 * Fetch all active care package products
 * @returns {Promise<Array>} products
 */
export const fetchProducts = async () => {
    const response = await axios.get(`${config.viewlogiesApiUrl}/client/care-package/products`, {
        withCredentials: true,
    });
    return response.data.data;
};

/**
 * Create a Stripe PaymentIntent (and pending order) for a care package purchase
 * The amount is computed server-side from the product
 * @returns {Promise<Object>} { data: { clientSecret, orderNumber, amounts } }
 */
export const createPaymentIntent = async (organizationId, eventId, productId, { message, fromName } = {}) => {
    const response = await axios.post(
        `${config.viewlogiesApiUrl}/client/event/${organizationId}/${eventId}/care-package/payment-intent`,
        { productId, message, fromName },
        { withCredentials: true },
    );
    return response.data;
};

/**
 * Update an order with contact details after payment confirmation
 * @returns {Promise<Object>} { data: { orderNumber } }
 */
export const submitOrder = async (orderNumber, { message, fromName, contact } = {}) => {
    const response = await axios.post(
        `${config.viewlogiesApiUrl}/client/care-package/order/${orderNumber}`,
        { message, fromName, contact },
        { withCredentials: true },
    );
    return response.data;
};

/**
 * Fetch the current status of an order (safe fields only)
 * @returns {Promise<Object>} { orderNumber, status, productId, eventId, createDate }
 */
export const fetchOrder = async orderNumber => {
    const response = await axios.get(`${config.viewlogiesApiUrl}/client/care-package/order/${orderNumber}`, {
        withCredentials: true,
    });
    return response.data.data;
};

/**
 * Retrieve the viewer's past care package orders for an event from the browser cookie
 * @param {String} eventId
 * @returns {Array<Object>} [{ orderNumber, productId, productName }]
 */
export const getCarePackageOrdersCookie = eventId => {
    const val = getCookie(`${eventId}_care_package_orders`);
    try {
        const orders = val ? JSON.parse(val) : [];
        return Array.isArray(orders) ? orders : [];
    } catch {
        return [];
    }
};

/**
 * Append a completed order to the viewer's past-orders cookie for an event
 * @param {String} eventId
 * @param {Object} order - { orderNumber, productId, productName }
 */
export const appendCarePackageOrderCookie = (eventId, order) => {
    const orders = getCarePackageOrdersCookie(eventId).filter(o => o.orderNumber !== order.orderNumber);
    orders.push(order);
    setCookie(`${eventId}_care_package_orders`, JSON.stringify(orders), 3650);
};
